import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { useT } from '../../hooks/useT';

const SCENARIO_COLORS = {
  realistic:   '#0ea5e9',
  optimistic:  '#10b981',
  pessimistic: '#f59e0b',
  deadline:    '#a855f7',
};

// Distinct palette for user-selected investment simulations
const SIM_COLORS = ['#fb923c', '#f472b6', '#34d399', '#818cf8', '#facc15'];

function formatCurrency(value, currency = 'USD') {
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
  return value?.toLocaleString(locale, { style: 'currency', currency, maximumFractionDigits: 0 });
}

/** Compound-growth curve for a given monthly rate over the projection rows. */
function computeSimCurve(projection, monthlyRatePct) {
  if (!projection?.length) return [];
  const rate = monthlyRatePct / 100;
  const startSavings = parseFloat(projection[0].accumulated_value) - parseFloat(projection[0].monthly_addition);
  let balance = startSavings;
  return projection.map((row) => {
    const interest = parseFloat((balance * rate).toFixed(2));
    balance = parseFloat((balance + interest + parseFloat(row.monthly_addition)).toFixed(2));
    return balance;
  });
}

function CustomTooltip({ active, payload, label, simulations, currency = 'USD' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-card border border-surface-border rounded-xl p-3 text-sm min-w-[180px]">
      <p className="text-slate-400 mb-2">Month {label}</p>
      {payload.map((entry) => {
        const isScenario = !entry.dataKey.startsWith('sim_');
        const simIdx = isScenario ? -1 : parseInt(entry.dataKey.replace('sim_', ''), 10);
        const label_ = isScenario
          ? entry.name
          : (simulations?.[simIdx]?.name ?? entry.name);
        return (
          <div key={entry.dataKey} className="flex justify-between gap-4">
            <span style={{ color: entry.color }}>{label_}</span>
            <span className="font-semibold text-slate-100">{formatCurrency(entry.value, currency)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function ScenarioChart({ scenarios, effectiveCost, simulations = [], projection = [], currency = 'USD' }) {
  const t = useT();
  if (!scenarios || scenarios.length === 0) return null;

  // Pre-compute simulation curves indexed by row
  const simCurves = simulations.map((sim) => computeSimCurve(projection, sim.monthly_rate_pct));

  // Build unified month axis covering all scenarios + simulations
  const maxScenarioMonths = Math.max(...scenarios.map((s) => s.estimated_months || 0), 12);
  const maxSimMonths = projection.length || 0;
  const maxMonths = Math.max(maxScenarioMonths, maxSimMonths);

  const data = [];
  for (let m = 1; m <= maxMonths; m++) {
    const point = { month: m };
    // AI scenarios
    scenarios.forEach((s) => {
      const entry = (s.monthly_projection || []).find((p) => p.month === m);
      if (entry) point[s.scenario_type] = entry.savings_balance;
    });
    // Investment simulations
    simulations.forEach((_, i) => {
      const val = simCurves[i]?.[m - 1];
      if (val !== undefined) point[`sim_${i}`] = val;
    });
    data.push(point);
  }

  return (
    <div>
      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        {scenarios.map((s) => (
          <div key={s.scenario_type} className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SCENARIO_COLORS[s.scenario_type] || '#888' }} />
            <span className="text-xs text-slate-400">{s.label}</span>
            <span className="text-xs text-slate-500">({s.estimated_months}mo)</span>
          </div>
        ))}
        {simulations.map((sim, i) => (
          <div key={`sim_${i}`} className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SIM_COLORS[i % SIM_COLORS.length] }} />
            <span className="text-xs text-slate-400">{sim.name}</span>
            <span className="text-xs text-slate-500">({sim.annual_rate_pct}%/yr)</span>
          </div>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
          <defs>
            {scenarios.map((s) => (
              <linearGradient key={s.scenario_type} id={`grad-${s.scenario_type}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={SCENARIO_COLORS[s.scenario_type]} stopOpacity={0.25} />
                <stop offset="95%" stopColor={SCENARIO_COLORS[s.scenario_type]} stopOpacity={0.02} />
              </linearGradient>
            ))}
            {simulations.map((_, i) => {
              const color = SIM_COLORS[i % SIM_COLORS.length];
              return (
                <linearGradient key={`sim_${i}`} id={`grad-sim_${i}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={color} stopOpacity={0.20} />
                  <stop offset="95%" stopColor={color} stopOpacity={0.02} />
                </linearGradient>
              );
            })}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <XAxis
            dataKey="month"
            tickLine={false}
            axisLine={false}
            tick={{ fill: '#64748b', fontSize: 11 }}
            tickFormatter={(v) => `M${v}`}
            interval={Math.floor(maxMonths / 8)}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fill: '#64748b', fontSize: 11 }}
            tickFormatter={(v) => {
              const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
              return (v / 1000).toLocaleString(locale, { style: 'currency', currency, maximumFractionDigits: 0 }) + 'k';
            }}
          />
          <Tooltip content={<CustomTooltip simulations={simulations} currency={currency} />} />
          {/* Goal target reference line */}
          {effectiveCost > 0 && (
            <ReferenceLine
              y={effectiveCost}
              stroke="#475569"
              strokeDasharray="6 3"
              label={{ value: t('chart.goal'), position: 'insideTopRight', fill: '#64748b', fontSize: 10 }}
            />
          )}
          {/* AI scenario areas */}
          {scenarios.map((s) => (
            <Area
              key={s.scenario_type}
              type="monotone"
              dataKey={s.scenario_type}
              name={s.label}
              stroke={SCENARIO_COLORS[s.scenario_type]}
              fill={`url(#grad-${s.scenario_type})`}
              strokeWidth={2}
              dot={false}
              connectNulls={false}
            />
          ))}
          {/* Investment simulation areas */}
          {simulations.map((sim, i) => {
            const color = SIM_COLORS[i % SIM_COLORS.length];
            return (
              <Area
                key={`sim_${i}`}
                type="monotone"
                dataKey={`sim_${i}`}
                name={sim.name}
                stroke={color}
                fill={`url(#grad-sim_${i})`}
                strokeWidth={2}
                strokeDasharray="5 3"
                dot={false}
                connectNulls={false}
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>

      {effectiveCost > 0 && (
        <p className="text-xs text-slate-500 text-center mt-2">
          {t('chart.hint.dashed')}
          {simulations.length === 0 && ' ' + t('chart.hint.select')}
        </p>
      )}
    </div>
  );
}
